import { useEffect, useState } from 'react';

import PaymentQuotas from '../../components/CardInputs/PaymentQuotas/PaymentQuotas';
import Promotions from '../../components/CardInputs/Promotions/Promotions';
import {
  EntryContainer,
  PromotionCard,
  PromotionsList,
} from './EntryStyledComponents';
import {
  CardsList,
  PaymentSelectText,
} from '../../components/CardInputs/CardsStyledComponents';

import { theme } from '../../styles/Theme';
import { colors } from '../../styles/Colors';
import FinancialService from '../../services/Financial';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES_PATH } from '../../constants';
import {
  DebtResponse,
  PromotionsData,
  StandardPaymentsData,
  TransactionReasonEnum,
} from '../../interfaces/Debt';
import { useDebtResult } from '../../contexts/DebtContext';
import { PaymentCard } from '../../interfaces/Loan';
import { usePaymentSelected } from '../../contexts/PaymentSelectedContext';
import { useUserResult } from '../../contexts/UserContext';
import {
  PendingPayment,
  ReusePaymentOptionalParams,
  ReusePaymentRequest,
} from '../../interfaces/Payment';
import { Transition } from '../Transition';
import TokenService from '../../services/Token';
import { useCardsResult } from '../../contexts/CardsContext';
import GAnalyticsService from '../../services/GAnalytics/ganalytics.service';
import dayjs from 'dayjs';
import { GTMFlowSteps } from '../../interfaces/GTM';
import { PaymentMethod } from '../../interfaces/PaymentMethod';
import { scrollToTop } from '../../utils/scrollHelper';
import CustomerHeader from '../../components/CustomerHeader/CustomerHeader';
import { ViewMode } from '../../interfaces/Mode';
import PaymentButtons from '../../components/EntryComponents/ContainerButtons/ContainerButtons';
import { saveUrlParams } from '../../utils/urlParams';
import { initTracking } from '@pepper/utm-react';
import { convertLeadToken } from '../../utils/convertLeadToken';
import { InsightService } from '@pepper/insights-react';
import { useLedgerResult } from '../../contexts/LedgerContext';
import { StorageService } from '../../services/Storage';
import {
  handleErrorPaymentClick,
  newPendingPayment,
  removePaymentMethodError,
} from '../../utils/paymentsHelper';
import { setClaritySessionId, setClarityUserId } from '../../services/Clarity';

const Entry: React.FC = () => {
  const search = useLocation().search;
  const urlParams = new URLSearchParams(search);

  const leadToken = urlParams.get('lead_token');
  sessionStorage.setItem('advance-fee', 'false');
  localStorage.setItem('come-from-nodebt', 'false');
  const viewMode = sessionStorage.getItem('pepper-view-mode');
  const flowCO = sessionStorage.getItem('flow') === 'co' || false;
  const pendingPayment: PendingPayment | null =
    JSON.parse(StorageService.getPendingPayment() ?? '{}') || null;

  const { white, red4 } = colors;
  const { textBold } = theme.fontWeight;
  const [cardList, setCardList] = useState<PaymentCard[]>([]);
  const [loading, setloading] = useState(true);

  const navigate = useNavigate();

  const { debtResult, createDebtResult, deleteDebtResult } = useDebtResult();
  const { paymentSelected, createPaymentSelected, deletePaymentSelected } =
    usePaymentSelected();
  const { userResult, createUserResult, deleteUserResult } = useUserResult();
  const { cardsResult, createCardsResult, deleteCardsResult } =
    useCardsResult();
  const [isOneQuotaDebt, setIsOneQuotaDebt] = useState(false);
  const [initTime] = useState<dayjs.Dayjs>(dayjs());

  const { createLedgerResult } = useLedgerResult();

  const getUserData = async (documentId: string) => {
    try {
      const ledgersFound = await FinancialService.searchLedgers({
        document_id: documentId,
        has_available_amount: true,
      });
      const ledgerInfo = ledgersFound.ledgers.find(
        (ledger) => ledger.ledger_id === pendingPayment?.ledger_id,
      );
      const expirationDate =
        pendingPayment && new Date(pendingPayment?.expiration_date);
      const now = new Date();
      if (
        ledgersFound.ledgers.length > 0 &&
        ledgerInfo &&
        ledgerInfo.available_amount > 0 &&
        expirationDate &&
        expirationDate > now
      ) {
        createLedgerResult(ledgersFound);
        navigate(ROUTES_PATH.PAYMENT_ON_COURSE);
      } else {
        if (
          pendingPayment &&
          ledgerInfo &&
          expirationDate &&
          expirationDate < now
        ) {
          StorageService.removePendingPayment();
          createLedgerResult({ ledgers: [] });
        }
        let debtInfo: DebtResponse;
        if (debtResult && !leadToken) {
          debtInfo = debtResult;
        } else {
          debtInfo = await FinancialService.getDebtData(documentId);
          createDebtResult(debtInfo);
          GAnalyticsService.saveDebtDataLayer(debtInfo);
        }
        if (debtInfo?.loans.length > 0) {
          if (
            debtInfo.loans.length === 1 &&
            debtInfo.loans[0].installments.length === 1
          ) {
            setIsOneQuotaDebt(true);
          }
          if (
            debtInfo.standard_payments.length === 0 &&
            debtInfo.promotions.length > 0
          ) {
            createPaymentSelected(debtInfo.promotions[0]);
          } else {
            createPaymentSelected(debtInfo.standard_payments[0]);
          }
          getPaymentCards(documentId);
        } else {
          navigate(ROUTES_PATH.NO_DEBT);
        }
      }
    } catch (error) {
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    } finally {
      setloading(false);
    }
  };
  const getPaymentCards = async (documentId: string) => {
    if (cardsResult) {
      setCardList(cardsResult.payment_methods);
    } else {
      try {
        const response = await FinancialService.getPaymentCards(documentId);
        createCardsResult(response);
        setCardList(response.payment_methods);
        GAnalyticsService.savePaymentMethodsDataLayer(response);
      } catch (error) {}
    }
  };

  const getUserInfo = async () => {
    try {
      const response = await FinancialService.getUserData();
      createUserResult(response);
      await getUserData(response.document_id);
      GAnalyticsService.saveCustomerDataLayer(response, cardList);
      if (response.person_id) {
        GAnalyticsService.saveUserId(response.person_id);
        setClarityUserId(response.person_id);
        setClaritySessionId(response.person_id);
      }
    } catch (error: any) {
      if (error?.response?.status) {
        navigate(ROUTES_PATH.TIMEOUT);
      } else {
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    }
  };

  const handlePaymentClick = async () => {
    if (userResult?.document_id && paymentSelected) {
      if (
        paymentSelected.transaction_reason === TransactionReasonEnum.POP ||
        paymentSelected.transaction_reason === TransactionReasonEnum.DEC ||
        paymentSelected.transaction_reason === TransactionReasonEnum.DEP ||
        paymentSelected.transaction_reason === TransactionReasonEnum.PAN
      ) {
        sessionStorage.setItem('able-to-advance-fee', 'false');
      } else {
        sessionStorage.setItem('able-to-advance-fee', 'true');
      }
      try {
        setloading(true);
        scrollToTop();
        const paymentReuseData: ReusePaymentRequest = {
          document_id: userResult.document_id,
          loan_id: paymentSelected?.loan_id,
          payment_method_id: cardList[0].payment_method_id,
          amount: paymentSelected?.amount,
          transaction_reason: paymentSelected?.transaction_reason,
          access_mode: 'FAST_PAY',
        };
        const response = await FinancialService.reusePayment(paymentReuseData);
        const optionalParams: ReusePaymentOptionalParams = {
          transaction_reason: paymentSelected.transaction_reason,
          excused_amount: paymentSelected.amount_excused ?? 0,
        };
        GAnalyticsService.saveReusePaymentDataLayer(response, optionalParams);
        GAnalyticsService.savePaymentCardType(cardList[0].type);
        if (initTime) {
          GAnalyticsService.sendFastPay(initTime, paymentSelected.type);
        }
        if (cardsResult && cardsResult.payment_methods.length > 0) {
          GAnalyticsService.savePaymentMethodsDataLayer(
            cardsResult,
            PaymentMethod.EXPRESS,
          );
        }
        if (response.status === 'OK') {
          GAnalyticsService.sendContinuePayment(initTime, GTMFlowSteps.ENTRY, {
            payment_method: PaymentMethod.EXPRESS,
            payment_transaction_reason: paymentReuseData.transaction_reason,
            payment_iin_number: cardList[0].iin_number,
            payment_card_brand: cardList[0].type,
            payment_third_used: '',
          });
          GAnalyticsService.sendPurchase({
            currency: 'EUR',
            transaction_id: response.loan_id,
            value: response.amount,
            items: [
              {
                itemId: `payment-${response.payment_method_id}`,
                itemName: 'Payment-Landing',
              },
            ],
          });
          InsightService.logTrace('Payment Click Success', [
            { PaymentMethod: response.payment_method_id },
            { amount: response.amount.toString() },
            { loan_id: response.loan_id },
            { transaction_reason: paymentSelected.transaction_reason },
            { type: response.transaction_type },
            { route: ROUTES_PATH.ENTRY },
          ]);
          navigate(ROUTES_PATH.SUCCESS);
          // Se guarda el pago completado en el local storage
          newPendingPayment(response);
        } else {
          cardList.shift();
          setCardList(cardList);
          handleErrorPaymentClick(
            navigate,
            initTime,
            paymentSelected,
            GTMFlowSteps.ENTRY,
            ROUTES_PATH.ENTRY,
          );
        }
      } catch (error) {
        setCardList([]);
        handleErrorPaymentClick(
          navigate,
          initTime,
          paymentSelected,
          GTMFlowSteps.ENTRY,
          ROUTES_PATH.ENTRY,
        );
      } finally {
        setloading(false);
      }
    } else {
      setCardList([]);
    }
  };

  const clearUser = () => {
    deleteDebtResult();
    deleteCardsResult();
    deletePaymentSelected();
    deleteUserResult();
    TokenService.clearSession();
  };

  const btnContinueDebtType = () => {
    if (paymentSelected) {
      GAnalyticsService.sendContinueDebtType(initTime, paymentSelected.type);
      navigate(ROUTES_PATH.TYPES_PAYMENTS);
    }
  };

  useEffect(() => {
    if (
      flowCO ||
      (debtResult &&
        debtResult.loans.length > 0 &&
        debtResult?.loans[0].certified_debt)
    ) {
      navigate(ROUTES_PATH.ENTRY_CO);
    }
  }, [debtResult]);

  useEffect(() => {
    if (!leadToken && userResult) {
      getUserData(userResult.document_id);
    } else {
      clearUser();
      getUserInfo();
    }

    if (urlParams.size > 0 && process.env.REACT_APP_ENV && leadToken) {
      const trackedData = saveUrlParams(urlParams);
      if (trackedData) {
        const decodedToken = convertLeadToken(leadToken);
        initTracking(
          process.env.REACT_APP_ENV,
          decodedToken,
          trackedData.pclient_type || 'lead_token',
          'Landing_Pagos',
        );
      }
    }

    if (sessionStorage.getItem('pepper-payment-method-error')) {
      removePaymentMethodError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {!loading && (
        <EntryContainer
          id="entry-page"
          $marginBottom={viewMode === ViewMode.IFRAME}
        >
          <CustomerHeader />
          <PaymentSelectText fontSize={16} color="gray0" lineHeight={22}>
            Selecciona qué quieres pagar:
          </PaymentSelectText>
          <CardsList id="standard-payments-list">
            {debtResult?.standard_payments.map(
              (payment: StandardPaymentsData, index) => {
                return (
                  <PaymentQuotas
                    key={index}
                    standardPayments={payment}
                    onSelectInput={() => {
                      createPaymentSelected(payment);
                    }}
                    isSelected={paymentSelected === payment}
                    isOneQuotaDebt={isOneQuotaDebt}
                  />
                );
              },
            )}
          </CardsList>
          {debtResult && debtResult.promotions.length > 0 && !flowCO && (
            <PromotionCard id="promotions-card">
              <PromotionsList id="promotions-list">
                {debtResult?.promotions.map((value: PromotionsData, index) => (
                  <Promotions
                    key={index}
                    promotion={value}
                    onSelectInput={() => {
                      createPaymentSelected(value);
                    }}
                    isSelected={paymentSelected === value}
                    loan={debtResult.loans.find(
                      (e) => e.loan_id === value.loan_id,
                    )}
                  />
                ))}
              </PromotionsList>
            </PromotionCard>
          )}
          <PaymentButtons
            cardList={cardList}
            white={white}
            red4={red4}
            textBold={textBold}
            paymentSelected={paymentSelected}
            paymentClickButton={cardList.length > 0}
            handlePaymentClick={handlePaymentClick}
            btnContinueDebtType={btnContinueDebtType}
          />
        </EntryContainer>
      )}
      {loading && <Transition />}
    </>
  );
};

export default Entry;
